/**
 * useHomeBootstrapStatus Hook
 * 
 * Monitors all critical bootstrap queries that must complete before
 * the post-login loader can be hidden.
 * 
 * Returns:
 * - isLoading: true if ANY of the bootstrap queries are still loading
 * 
 * Bootstrap queries monitored:
 * - settings/bootstrap/app RPC call (useAppBootstrap)
 * - home catalog query (useHomeCatalog)
 * - home preferences query (useHomePrefs)
 * - company features query (if exists)
 */

import { useAppBootstrap } from '../lib/query/bootstrapHooks';
import { useHomeCatalog } from './queries/useHomeCatalog';
import { useHomePrefs } from './queries/useHomePrefs';
import { useParams } from 'react-router-dom';

export interface HomeBootstrapStatus {
  isLoading: boolean;
}

/**
 * Hook to check if home bootstrap queries are still loading
 * 
 * @param company - Company slug (optional, will be extracted from URL if not provided)
 * @returns Object with isLoading boolean
 * 
 * @example
 * ```tsx
 * const { isLoading } = useHomeBootstrapStatus();
 * if (isLoading) {
 *   // Still loading bootstrap data
 * }
 * ```
 */
export function useHomeBootstrapStatus(
  company?: string
): HomeBootstrapStatus {
  const { company: companyFromParams } = useParams<{ company?: string }>();
  const finalCompany = company || companyFromParams;

  // CRITICAL: Check if token exists before enabling queries
  // This prevents "invalid token" errors
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('token');

  // Monitor bootstrap/app RPC call
  // CRITICAL: Only enable if company exists AND token is available
  const { isLoading: bootstrapLoading, isFetching: bootstrapFetching } = useAppBootstrap(
    finalCompany || undefined,
    {
      enabled: !!finalCompany && finalCompany !== 'undefined' && hasToken,
      refetchOnMount: true,
    }
  );

  // Monitor home catalog query
  const { isLoading: catalogLoading, isFetching: catalogFetching } = useHomeCatalog(
    finalCompany || undefined,
    {
      enabled: !!finalCompany && finalCompany !== 'undefined' && hasToken,
      refetchOnMount: true,
    }
  );

  // Monitor home preferences query
  const { isLoading: prefsLoading, isFetching: prefsFetching } = useHomePrefs(
    finalCompany || undefined,
    {
      enabled: !!finalCompany && finalCompany !== 'undefined' && hasToken,
      refetchOnMount: true,
    }
  );

  // Check if ANY query is still loading or fetching
  const isLoading = 
    bootstrapLoading || 
    bootstrapFetching || 
    catalogLoading || 
    catalogFetching || 
    prefsLoading || 
    prefsFetching;

  return {
    isLoading,
  };
}

